import { useEffect, useRef } from "react";
import { logger } from "@/lib/logger";

/**
 * Lazy-loads the Google Maps JS SDK with the Places library — exactly once per
 * page lifetime, no matter how many autocomplete inputs are mounted.
 * Returns a promise that resolves with `window.google.maps` once ready.
 *
 * No external dependency, no bundler plug-in. The browser handles the script
 * tag so React's render loop never blocks.
 */
let _gmaps = null;

export function loadGoogleMaps(apiKey) {
  if (typeof window === "undefined") return Promise.reject(new Error("ssr"));
  if (window.google?.maps?.places) return Promise.resolve(window.google.maps);
  if (_gmaps) return _gmaps;

  _gmaps = new Promise((resolve, reject) => {
    if (!apiKey) {
      reject(new Error("Missing REACT_APP_GOOGLE_MAPS_API_KEY"));
      return;
    }
    const existing = document.getElementById("gmaps-places-sdk");
    if (existing) {
      existing.addEventListener("load", () => resolve(window.google.maps));
      existing.addEventListener("error", () => reject(new Error("script failed")));
      return;
    }
    const s = document.createElement("script");
    s.id = "gmaps-places-sdk";
    s.async = true;
    s.defer = true;
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&v=weekly&callback=__wynridgeGmapsReady`;
    window.__wynridgeGmapsReady = () => resolve(window.google.maps);
    s.onerror = () => {
      _gmaps = null;
      reject(new Error("Google Maps script failed to load"));
    };
    document.head.appendChild(s);
  });
  return _gmaps;
}

/**
 * React hook: attach a Places Autocomplete to an input ref.
 * - `onSelect(formattedAddress)` fires when the user picks a suggestion
 * - `onInput(value)` fires on every keystroke so the parent state stays in sync
 * - Biased to the US (~50mi around Goodlettsville, TN by default).
 * - Falls back silently to plain input behaviour if the SDK fails or no key.
 */
export function useGoogleAddressAutocomplete({
  inputRef,
  onSelect,
  biasCenter = { lat: 36.3231, lng: -86.7417 }, // Goodlettsville, TN
  biasRadiusMeters = 80000, // ~50 mi
}) {
  const acRef = useRef(null);

  useEffect(() => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    const input = inputRef.current;
    if (!input || !apiKey) return undefined;

    let cancelled = false;
    let listener = null;

    loadGoogleMaps(apiKey)
      .then((maps) => {
        if (cancelled || !maps?.places) return;
        if (!document.body.contains(input)) return;
        try {
          const latDelta = biasRadiusMeters / 111_320;
          const lngDelta =
            biasRadiusMeters / (111_320 * Math.cos((biasCenter.lat * Math.PI) / 180));
          const bounds = new maps.LatLngBounds(
            { lat: biasCenter.lat - latDelta, lng: biasCenter.lng - lngDelta },
            { lat: biasCenter.lat + latDelta, lng: biasCenter.lng + lngDelta }
          );
          const ac = new maps.places.Autocomplete(input, {
            componentRestrictions: { country: "us" },
            fields: ["formatted_address", "address_components", "geometry"],
            types: ["address"],
            bounds,
            strictBounds: false,
          });
          acRef.current = ac;
          listener = ac.addListener("place_changed", () => {
            const place = ac.getPlace();
            const addr = place?.formatted_address || input.value;
            if (addr && typeof onSelect === "function") onSelect(addr);
          });
        } catch (e) {
          logger.warn("Google Places Autocomplete attach failed:", e?.message || e);
        }
      })
      .catch((e) => {
        logger.warn("Google Places Autocomplete unavailable:", e?.message || e);
      });

    return () => {
      cancelled = true;
      try {
        if (listener && window.google?.maps?.event) {
          window.google.maps.event.removeListener(listener);
        }
      } catch (e) {
        logger.warn("Google Places Autocomplete cleanup failed:", e?.message || e);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
